import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { invoicesAPI, tasksAPI, teamAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, DollarSign, CheckSquare, Loader2, Calendar, User, Building2, 
  Download, CreditCard, Banknote, ArrowRightLeft, FileSpreadsheet, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Filter
} from 'lucide-react';
import { format, isAfter, isBefore, subDays, differenceInDays } from 'date-fns';
import * as XLSX from 'xlsx';

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [accountsReceivable, setAccountsReceivable] = useState({ invoices: [], totalReceivable: 0 });
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  // AR Report state
  const [arReportDate, setArReportDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [arSortBy, setArSortBy] = useState('total');
  const [arSortOrder, setArSortOrder] = useState('desc');
  const [arData, setArData] = useState([]);

  // Payment Report state
  const [paymentStartDate, setPaymentStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [paymentEndDate, setPaymentEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [paymentSortBy, setPaymentSortBy] = useState('paidDate');
  const [paymentSortOrder, setPaymentSortOrder] = useState('desc');

  // Tasks Report state
  const [taskTeamFilter, setTaskTeamFilter] = useState('all');
  const [taskSortBy, setTaskSortBy] = useState('dueDate');
  const [taskSortOrder, setTaskSortOrder] = useState('asc');

  // Active tab
  const [activeReport, setActiveReport] = useState('ar');

  useEffect(() => {
    fetchReportData();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    calculateARAgingReport();
  }, [arReportDate, accountsReceivable]);

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      
      // Fetch all invoices for AR calculations
      const arResponse = await invoicesAPI.getAccountsReceivable();
      setAccountsReceivable(arResponse.data);
      
      // Fetch paid invoices
      const paidResponse = await invoicesAPI.getAll({ status: 'paid' });
      setPaidInvoices(paidResponse.data.invoices || []);
      
      // Fetch pending tasks
      const tasksResponse = await tasksAPI.getAll({ status: 'pending' });
      const inProgressResponse = await tasksAPI.getAll({ status: 'in_progress' });
      
      const allPendingTasks = [
        ...(tasksResponse.data.tasks || []),
        ...(inProgressResponse.data.tasks || [])
      ];
      
      setPendingTasks(allPendingTasks);
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast({ title: 'Error', description: 'Failed to load report data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Calculate AR Aging Report
  const calculateARAgingReport = () => {
    if (!accountsReceivable.invoices) return;

    const reportDate = new Date(arReportDate);
    const clientBalances = {};

    accountsReceivable.invoices.forEach(invoice => {
      const dueDate = new Date(invoice.dueDate);
      const invoiceDate = new Date(invoice.invoiceDate);
      
      // Only include invoices issued before or on report date
      if (isAfter(invoiceDate, reportDate)) return;

      const clientName = invoice.clientName || 'Unknown';
      // Use outstanding balance (grandTotal - paidAmount) instead of grandTotal
      const grandTotal = invoice.grandTotal || 0;
      const paidAmount = invoice.paidAmount || 0;
      const amount = invoice.outstandingBalance !== undefined 
        ? invoice.outstandingBalance 
        : (grandTotal - paidAmount);
      
      // Skip if fully paid
      if (amount <= 0) return;
      
      const daysOverdue = differenceInDays(reportDate, dueDate);

      if (!clientBalances[clientName]) {
        clientBalances[clientName] = {
          clientName,
          current: 0,
          days30: 0,
          days60: 0,
          days90Plus: 0,
          total: 0
        };
      }

      if (daysOverdue <= 0) {
        clientBalances[clientName].current += amount;
      } else if (daysOverdue <= 30) {
        clientBalances[clientName].days30 += amount;
      } else if (daysOverdue <= 60) {
        clientBalances[clientName].days60 += amount;
      } else {
        clientBalances[clientName].days90Plus += amount;
      }
      clientBalances[clientName].total += amount;
    });

    setArData(Object.values(clientBalances));
  };

  // Sorting functions
  const sortedARData = [...arData].sort((a, b) => {
    let comparison = 0;
    if (arSortBy === 'clientName') {
      comparison = a.clientName.localeCompare(b.clientName);
    } else {
      comparison = a[arSortBy] - b[arSortBy];
    }
    return arSortOrder === 'asc' ? comparison : -comparison;
  });

  const filteredPaidInvoices = paidInvoices
    .filter(inv => {
      const paidDate = new Date(inv.paidDate);
      const startDate = new Date(paymentStartDate);
      const endDate = new Date(paymentEndDate);
      endDate.setHours(23, 59, 59);
      
      if (paidDate < startDate || paidDate > endDate) return false;
      if (paymentModeFilter !== 'all' && inv.paymentMode !== paymentModeFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (paymentSortBy === 'clientName') {
        comparison = (a.clientName || '').localeCompare(b.clientName || '');
      } else if (paymentSortBy === 'paidDate') {
        comparison = new Date(a.paidDate) - new Date(b.paidDate);
      } else if (paymentSortBy === 'grandTotal') {
        comparison = a.grandTotal - b.grandTotal;
      }
      return paymentSortOrder === 'asc' ? comparison : -comparison;
    });

  const filteredTasks = pendingTasks
    .filter(task => {
      if (taskTeamFilter !== 'all' && task.assignedTo !== taskTeamFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (taskSortBy === 'dueDate') {
        comparison = new Date(a.dueDate) - new Date(b.dueDate);
      } else if (taskSortBy === 'title') {
        comparison = a.title.localeCompare(b.title);
      } else if (taskSortBy === 'priority') {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        comparison = (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
      } else if (taskSortBy === 'client') {
        comparison = (a.client || '').localeCompare(b.client || '');
      }
      return taskSortOrder === 'asc' ? comparison : -comparison;
    });

  // Payment totals
  const filteredPaidTotal = filteredPaidInvoices.reduce((sum, inv) => sum + (inv.grandTotal || 0), 0);

  // Helper functions
  const getPaymentModeLabel = (mode) => {
    switch (mode) {
      case 'cash': return 'Cash';
      case 'credit_card': return 'Credit Card';
      case 'etransfer': return 'E-Transfer';
      default: return mode || 'N/A';
    }
  };

  const getPaymentModeIcon = (mode) => {
    switch (mode) {
      case 'cash': return <Banknote className="h-4 w-4" />;
      case 'credit_card': return <CreditCard className="h-4 w-4" />;
      case 'etransfer': return <ArrowRightLeft className="h-4 w-4" />;
      default: return <DollarSign className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  const getTeamMemberName = (id) => {
    const member = teamMembers.find(m => m._id === id);
    return member?.name || id || 'Unassigned';
  };

  const toggleSort = (field, currentSort, setSort, currentOrder, setOrder) => {
    if (currentSort === field) {
      setOrder(currentOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('asc');
    }
  };

  const SortIcon = ({ field, currentSort, currentOrder }) => {
    if (currentSort !== field) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return currentOrder === 'asc' ? 
      <ArrowUp className="h-4 w-4 text-blue-600" /> : 
      <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  // Export functions
  const exportARToExcel = () => {
    if (sortedARData.length === 0) {
      toast({ title: 'No Data', description: 'No accounts receivable data to export', variant: 'destructive' });
      return;
    }

    // Keep values as numbers for Excel calculations
    const data = sortedARData.map(row => ({
      'Customer': row.clientName,
      'Current': Math.round(row.current * 100) / 100,
      '30 Days': Math.round(row.days30 * 100) / 100,
      '60 Days': Math.round(row.days60 * 100) / 100,
      '90+ Days': Math.round(row.days90Plus * 100) / 100,
      'Total': Math.round(row.total * 100) / 100
    }));

    // Add totals row
    const totals = sortedARData.reduce((acc, row) => ({
      current: acc.current + row.current,
      days30: acc.days30 + row.days30,
      days60: acc.days60 + row.days60,
      days90Plus: acc.days90Plus + row.days90Plus,
      total: acc.total + row.total
    }), { current: 0, days30: 0, days60: 0, days90Plus: 0, total: 0 });

    data.push({
      'Customer': 'TOTAL',
      'Current': Math.round(totals.current * 100) / 100,
      '30 Days': Math.round(totals.days30 * 100) / 100,
      '60 Days': Math.round(totals.days60 * 100) / 100,
      '90+ Days': Math.round(totals.days90Plus * 100) / 100,
      'Total': Math.round(totals.total * 100) / 100
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'AR Aging');
    
    ws['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    
    // Format numeric columns with currency format
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; R++) {
      ['B', 'C', 'D', 'E', 'F'].forEach(col => {
        const cell = ws[col + (R + 1)];
        if (cell && typeof cell.v === 'number') {
          cell.z = '$#,##0.00';
        }
      });
    }
    
    XLSX.writeFile(wb, `AR_Aging_Report_${arReportDate}.xlsx`);
    toast({ title: 'Success', description: 'AR Aging report exported' });
  };

  const exportPaymentsToExcel = () => {
    if (filteredPaidInvoices.length === 0) {
      toast({ title: 'No Data', description: 'No payment data to export', variant: 'destructive' });
      return;
    }

    // Keep amounts as numbers for Excel calculations
    const data = filteredPaidInvoices.map(invoice => ({
      'Invoice #': invoice.invoiceNumber,
      'Client': invoice.clientName,
      'Invoice Date': invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'MMM dd, yyyy') : '',
      'Paid Date': invoice.paidDate ? format(new Date(invoice.paidDate), 'MMM dd, yyyy') : '',
      'Payment Mode': getPaymentModeLabel(invoice.paymentMode),
      'Amount': invoice.grandTotal || 0
    }));

    data.push({
      'Invoice #': '',
      'Client': '',
      'Invoice Date': '',
      'Paid Date': '',
      'Payment Mode': 'Total:',
      'Amount': filteredPaidTotal
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    
    ws['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }];
    
    // Format Amount column with currency format
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; R++) {
      const cell = ws['F' + (R + 1)];
      if (cell && typeof cell.v === 'number') {
        cell.z = '$#,##0.00';
      }
    }
    
    XLSX.writeFile(wb, `Payment_Report_${paymentStartDate}_to_${paymentEndDate}.xlsx`);
    toast({ title: 'Success', description: 'Payment report exported' });
  };

  const exportTasksToExcel = () => {
    if (filteredTasks.length === 0) {
      toast({ title: 'No Data', description: 'No tasks to export', variant: 'destructive' });
      return;
    }

    const data = filteredTasks.map(task => ({
      'Task Name': task.title,
      'Client': task.client || '',
      'Due Date': task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : '',
      'Team Member': getTeamMemberName(task.assignedTo),
      'Priority': task.priority || '',
      'Status': task.status?.replace('_', ' ') || '',
      'Overdue': isOverdue(task.dueDate) ? 'Yes' : 'No'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pending Tasks');
    
    ws['!cols'] = [{ wch: 30 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
    
    XLSX.writeFile(wb, `Pending_Tasks_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast({ title: 'Success', description: 'Tasks report exported' });
  };

  // AR totals
  const arTotals = sortedARData.reduce((acc, row) => ({
    current: acc.current + row.current,
    days30: acc.days30 + row.days30,
    days60: acc.days60 + row.days60,
    days90Plus: acc.days90Plus + row.days90Plus,
    total: acc.total + row.total
  }), { current: 0, days30: 0, days60: 0, days90Plus: 0, total: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-500 mt-1">View financial and task reports</p>
      </div>

      {/* Report Cards - Tile Navigation */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Production Report Link */}
        <Link to="/reports/production">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300 transition-colors cursor-pointer h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm">Accounting Production</p>
                  <p className="text-xs text-gray-500">Production & Collections</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* AR Report */}
        <Card 
          className={`cursor-pointer transition-colors ${activeReport === 'ar' ? 'border-red-300 bg-red-50' : 'hover:border-red-200'}`}
          onClick={() => setActiveReport('ar')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">Accounts Receivable</p>
                <p className="text-xs text-gray-500">${arTotals.total.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Report */}
        <Card 
          className={`cursor-pointer transition-colors ${activeReport === 'payment' ? 'border-green-300 bg-green-50' : 'hover:border-green-200'}`}
          onClick={() => setActiveReport('payment')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckSquare className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">Payment Report</p>
                <p className="text-xs text-gray-500">${filteredPaidTotal.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Report */}
        <Card 
          className={`cursor-pointer transition-colors ${activeReport === 'tasks' ? 'border-orange-300 bg-orange-50' : 'hover:border-orange-200'}`}
          onClick={() => setActiveReport('tasks')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CheckSquare className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">Pending Tasks</p>
                <p className="text-xs text-gray-500">{filteredTasks.length} tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AR Report */}
      {activeReport === 'ar' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-red-600" />
                Accounts Receivable Aging Report
              </CardTitle>
              <Button onClick={exportARToExcel} variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" /> Export Excel
              </Button>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <Label>Report as of:</Label>
                <Input
                  type="date"
                  value={arReportDate}
                  onChange={(e) => setArReportDate(e.target.value)}
                  className="w-40"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('clientName', arSortBy, setArSortBy, arSortOrder, setArSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Customer <SortIcon field="clientName" currentSort={arSortBy} currentOrder={arSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('current', arSortBy, setArSortBy, arSortOrder, setArSortOrder)}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Current <SortIcon field="current" currentSort={arSortBy} currentOrder={arSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('days30', arSortBy, setArSortBy, arSortOrder, setArSortOrder)}
                    >
                      <div className="flex items-center justify-end gap-1">
                        30 Days <SortIcon field="days30" currentSort={arSortBy} currentOrder={arSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('days60', arSortBy, setArSortBy, arSortOrder, setArSortOrder)}
                    >
                      <div className="flex items-center justify-end gap-1">
                        60 Days <SortIcon field="days60" currentSort={arSortBy} currentOrder={arSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('days90Plus', arSortBy, setArSortBy, arSortOrder, setArSortOrder)}
                    >
                      <div className="flex items-center justify-end gap-1">
                        90+ Days <SortIcon field="days90Plus" currentSort={arSortBy} currentOrder={arSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('total', arSortBy, setArSortBy, arSortOrder, setArSortOrder)}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Total <SortIcon field="total" currentSort={arSortBy} currentOrder={arSortOrder} />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedARData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No outstanding receivables as of {format(new Date(arReportDate), 'MMM dd, yyyy')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {sortedARData.map((row, idx) => (
                        <TableRow key={idx} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{row.clientName}</TableCell>
                          <TableCell className="text-right">${row.current.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{row.days30 > 0 ? `$${row.days30.toFixed(2)}` : '-'}</TableCell>
                          <TableCell className="text-right">{row.days60 > 0 ? `$${row.days60.toFixed(2)}` : '-'}</TableCell>
                          <TableCell className="text-right text-red-600 font-medium">{row.days90Plus > 0 ? `$${row.days90Plus.toFixed(2)}` : '-'}</TableCell>
                          <TableCell className="text-right font-semibold">${row.total.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-gray-100 font-bold">
                        <TableCell>TOTAL</TableCell>
                        <TableCell className="text-right">${arTotals.current.toFixed(2)}</TableCell>
                        <TableCell className="text-right">${arTotals.days30.toFixed(2)}</TableCell>
                        <TableCell className="text-right">${arTotals.days60.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-red-600">${arTotals.days90Plus.toFixed(2)}</TableCell>
                        <TableCell className="text-right">${arTotals.total.toFixed(2)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Report */}
      {activeReport === 'payment' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-green-600" />
                Payment Report
              </CardTitle>
              <Button onClick={exportPaymentsToExcel} variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" /> Export Excel
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <Label>From:</Label>
                <Input
                  type="date"
                  value={paymentStartDate}
                  onChange={(e) => setPaymentStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>To:</Label>
                <Input
                  type="date"
                  value={paymentEndDate}
                  onChange={(e) => setPaymentEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>Payment Method:</Label>
                <Select value={paymentModeFilter} onValueChange={setPaymentModeFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="etransfer">E-Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 text-right">
              <span className="text-gray-500">Total:</span>
              <span className="text-2xl font-bold text-green-600 ml-2">${filteredPaidTotal.toFixed(2)}</span>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Invoice #</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('clientName', paymentSortBy, setPaymentSortBy, paymentSortOrder, setPaymentSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Client <SortIcon field="clientName" currentSort={paymentSortBy} currentOrder={paymentSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('paidDate', paymentSortBy, setPaymentSortBy, paymentSortOrder, setPaymentSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Paid Date <SortIcon field="paidDate" currentSort={paymentSortBy} currentOrder={paymentSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('grandTotal', paymentSortBy, setPaymentSortBy, paymentSortOrder, setPaymentSortOrder)}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Amount <SortIcon field="grandTotal" currentSort={paymentSortBy} currentOrder={paymentSortOrder} />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPaidInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No payments found for the selected period
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPaidInvoices.map((invoice) => (
                      <TableRow key={invoice._id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                        <TableCell>{invoice.clientName}</TableCell>
                        <TableCell>{invoice.paidDate ? format(new Date(invoice.paidDate), 'MMM dd, yyyy') : '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getPaymentModeIcon(invoice.paymentMode)}
                            {getPaymentModeLabel(invoice.paymentMode)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">${invoice.grandTotal?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks Report */}
      {activeReport === 'tasks' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-orange-600" />
                Pending Tasks Report
              </CardTitle>
              <Button onClick={exportTasksToExcel} variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" /> Export Excel
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <Label>Team Member:</Label>
                <Select value={taskTeamFilter} onValueChange={setTaskTeamFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team Members</SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member._id} value={member._id}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <span className="text-gray-500">Total:</span>
              <span className="text-2xl font-bold text-orange-600 ml-2">{filteredTasks.length} tasks</span>
              <span className="text-sm text-red-500 ml-4">
                ({filteredTasks.filter(t => isOverdue(t.dueDate)).length} overdue)
              </span>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('title', taskSortBy, setTaskSortBy, taskSortOrder, setTaskSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Task <SortIcon field="title" currentSort={taskSortBy} currentOrder={taskSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('client', taskSortBy, setTaskSortBy, taskSortOrder, setTaskSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Client <SortIcon field="client" currentSort={taskSortBy} currentOrder={taskSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('dueDate', taskSortBy, setTaskSortBy, taskSortOrder, setTaskSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Due Date <SortIcon field="dueDate" currentSort={taskSortBy} currentOrder={taskSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead>Team Member</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleSort('priority', taskSortBy, setTaskSortBy, taskSortOrder, setTaskSortOrder)}
                    >
                      <div className="flex items-center gap-1">
                        Priority <SortIcon field="priority" currentSort={taskSortBy} currentOrder={taskSortOrder} />
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No pending tasks found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTasks.map((task) => (
                      <TableRow key={task._id} className={`hover:bg-gray-50 ${isOverdue(task.dueDate) ? 'bg-red-50' : ''}`}>
                        <TableCell className="font-medium">{task.title}</TableCell>
                        <TableCell>{task.client || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : '-'}
                            {isOverdue(task.dueDate) && (
                              <Badge variant="destructive" className="text-xs">Overdue</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getTeamMemberName(task.assignedTo)}</TableCell>
                        <TableCell>
                          <Badge variant={getPriorityColor(task.priority)}>{task.priority || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={task.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}>
                            {task.status?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
